/**
 * Canonical CRM module manifest shared by the API worker and the web app.
 *
 * Every module's key, collection, web route, API route prefixes and
 * dependencies are declared once here. Both apps derive module settings
 * normalization, dependency resolution, landing paths and server-side
 * module enforcement from this file.
 */

export const CRM_MODULE_KEYS = [
  'contacts',
  'companies',
  'deals',
  'pipeline',
  'tasks',
  'inbox',
  'portal_connections',
  'portal_leads',
] as const;

export type CrmModuleKey = (typeof CRM_MODULE_KEYS)[number];

export const CRM_MODULE_COLLECTION_KEYS = ['shared_core', 'shared_workflows', 'real_estate'] as const;

export type CrmModuleCollectionKey = (typeof CRM_MODULE_COLLECTION_KEYS)[number];

export type CrmModuleCollectionKind = 'shared' | 'vertical';

export type CrmModuleSettings = Record<CrmModuleKey, boolean>;

export interface CrmRoleDefault {
  role_key: string;
  landing_path: string;
}

export interface CrmModuleManifestEntry {
  key: CrmModuleKey;
  collection: CrmModuleCollectionKey;
  /** Web route that hosts the module. */
  path: string;
  /** API path prefixes (below `/api`) served only while the module is enabled. */
  apiPrefixes: string[];
  dependencies: CrmModuleKey[];
}

export const CRM_MODULE_COLLECTIONS: Record<CrmModuleCollectionKey, { kind: CrmModuleCollectionKind }> = {
  shared_core: { kind: 'shared' },
  shared_workflows: { kind: 'shared' },
  real_estate: { kind: 'vertical' },
};

export const CRM_MODULES: CrmModuleManifestEntry[] = [
  {
    key: 'contacts',
    collection: 'shared_core',
    path: '/contacts',
    // Contact points, the contactability ledger, eligibility and interactions are contact data.
    apiPrefixes: ['/contacts', '/contact-points', '/contactability', '/eligibility', '/interactions'],
    dependencies: [],
  },
  { key: 'companies', collection: 'shared_core', path: '/companies', apiPrefixes: ['/companies'], dependencies: [] },
  { key: 'deals', collection: 'shared_core', path: '/deals', apiPrefixes: ['/deals'], dependencies: [] },
  { key: 'pipeline', collection: 'shared_core', path: '/pipeline', apiPrefixes: ['/pipeline'], dependencies: [] },
  { key: 'tasks', collection: 'shared_workflows', path: '/tasks', apiPrefixes: ['/tasks'], dependencies: [] },
  { key: 'inbox', collection: 'shared_workflows', path: '/inbox', apiPrefixes: ['/tickets'], dependencies: [] },
  {
    key: 'portal_connections',
    collection: 'real_estate',
    path: '/portal-connections',
    apiPrefixes: ['/portal-connections', '/portal-sync'],
    dependencies: [],
  },
  {
    key: 'portal_leads',
    collection: 'real_estate',
    path: '/portal-leads',
    apiPrefixes: ['/portal-leads', '/lead-routing'],
    dependencies: ['portal_connections'],
  },
];

export const CRM_MODULE_PRESET_KEYS = ['core_crm', 'sales_only', 'real_estate'] as const;

export type CrmModulePresetKey = (typeof CRM_MODULE_PRESET_KEYS)[number];

export const CRM_MODULE_PRESETS: Record<CrmModulePresetKey, CrmModuleKey[]> = {
  core_crm: ['contacts', 'companies', 'deals', 'pipeline', 'tasks', 'inbox'],
  sales_only: ['contacts', 'companies', 'deals', 'tasks', 'inbox'],
  real_estate: [...CRM_MODULE_KEYS],
};

export const DASHBOARD_PATH = '/dashboard';

const MODULE_BY_KEY = new Map(CRM_MODULES.map(module => [module.key, module]));

export function getCrmModule(key: CrmModuleKey): CrmModuleManifestEntry {
  const module = MODULE_BY_KEY.get(key);
  if (!module) throw new Error(`Unknown CRM module: ${key}`);
  return module;
}

export function buildDefaultModuleSettings(): CrmModuleSettings {
  return Object.fromEntries(CRM_MODULE_KEYS.map(key => [key, true])) as CrmModuleSettings;
}

export function resolveModuleSettingsDependencies(settings: CrmModuleSettings): CrmModuleSettings {
  const resolved = { ...settings };
  let changed = true;

  while (changed) {
    changed = false;
    for (const module of CRM_MODULES) {
      if (!resolved[module.key]) continue;
      for (const dependency of module.dependencies) {
        if (!resolved[dependency]) {
          resolved[dependency] = true;
          changed = true;
        }
      }
    }
  }

  return resolved;
}

export function buildPresetModuleSettings(presetKey: CrmModulePresetKey): CrmModuleSettings {
  const enabled = new Set(CRM_MODULE_PRESETS[presetKey]);
  return resolveModuleSettingsDependencies(Object.fromEntries(CRM_MODULE_KEYS.map(key => [key, enabled.has(key)])) as CrmModuleSettings);
}

/** Normalize untrusted input into a complete, dependency-resolved settings map. */
export function normalizeModuleSettings(input: Partial<Record<string, unknown>>): CrmModuleSettings {
  const settings = buildDefaultModuleSettings();
  for (const key of CRM_MODULE_KEYS) {
    const value = input[key];
    if (typeof value === 'boolean') settings[key] = value;
    else if (value === 'true') settings[key] = true;
    else if (value === 'false') settings[key] = false;
  }
  return resolveModuleSettingsDependencies(settings);
}

export function isLandingPathAvailable(path: string, settings: CrmModuleSettings): boolean {
  if (path === DASHBOARD_PATH) return true;
  return CRM_MODULES.some(module => module.path === path && settings[module.key]);
}

export function getDefaultRoleDefaults(settings: CrmModuleSettings): CrmRoleDefault[] {
  return [
    { role_key: 'admin', landing_path: DASHBOARD_PATH },
    { role_key: 'agent', landing_path: settings.portal_leads ? '/portal-leads' : DASHBOARD_PATH },
  ];
}

export function normalizeRoleDefaults(
  settings: CrmModuleSettings,
  input?: Array<{ role_key?: string | null; landing_path?: string | null }> | null
): CrmRoleDefault[] {
  const byRole = new Map(getDefaultRoleDefaults(settings).map(item => [item.role_key, item]));

  for (const item of input || []) {
    const roleKey = String(item?.role_key || '')
      .trim()
      .toLowerCase();
    const landingPath = String(item?.landing_path || '').trim();
    if (!roleKey || !landingPath) continue;
    byRole.set(roleKey, {
      role_key: roleKey,
      landing_path: isLandingPathAvailable(landingPath, settings) ? landingPath : byRole.get(roleKey)?.landing_path || DASHBOARD_PATH,
    });
  }

  return Array.from(byRole.values());
}

export function getDefaultLandingPath(settings: CrmModuleSettings, roleDefaults?: CrmRoleDefault[] | null, role?: string | null): string {
  const normalizedRole = String(role || '').toLowerCase();
  if (normalizedRole) {
    const roleDefault = normalizeRoleDefaults(settings, roleDefaults).find(item => item.role_key === normalizedRole);
    if (roleDefault) return roleDefault.landing_path;
  }
  return DASHBOARD_PATH;
}

/**
 * Resolve the module that owns an API path (e.g. `/api/listings/abc/media`).
 * The longest matching prefix wins, so a nested prefix is attributed to its
 * own module rather than a shorter enclosing one.
 */
export function getModuleForApiPath(pathname: string): CrmModuleKey | null {
  const path = pathname.startsWith('/api/') ? pathname.slice(4) : pathname;
  let match: { key: CrmModuleKey; length: number } | null = null;

  for (const module of CRM_MODULES) {
    for (const prefix of module.apiPrefixes) {
      if ((path === prefix || path.startsWith(`${prefix}/`)) && prefix.length > (match?.length ?? 0)) {
        match = { key: module.key, length: prefix.length };
      }
    }
  }

  return match?.key ?? null;
}
